import React, { Component } from 'react';
import { keyBy } from 'lodash';
import { Flex, Text, Box } from 'core/components';
import AdminTable from 'app/components/admin/AdminTable';
import SiteLink from 'app/components/links/SiteLink';
import InterfaceCollection from 'app/stores/interface/InterfaceCollection';
import { InterfaceNameSnmpIdAndDescription } from 'app/components/Interface';
import DeviceLink from 'app/components/links/DeviceLink';
import InterfaceClassificationKv from 'app/components/interfaces/InterfaceClassificationKv';
import { formatBytesGreek } from 'core/util/greekPrefixing';
import { inject, observer } from 'mobx-react';
import { InterfaceOperAdminStatusTag } from 'app/views/metrics/tables/StatusIndicators';
import { METRICS_STATUS_LABEL_MAP } from 'app/stores/metrics/metricsConstants';
import { deviceIndex } from 'app/util/lookups/deviceIndex';
import ToolResultCard from '../components/ToolResultCard';

const Overridden = ({ isOverridden }) => {
  if (!isOverridden) {
    return null;
  }

  return <Text muted>(Overridden)</Text>;
};

const ColumnOverrideRendererHOC =
  (field) =>
  ({ model, value }) => {
    const hasOverridden = model.overriddenFields.includes(field);

    return (
      <>
        {value}
        <Overridden isOverridden={hasOverridden} />
      </>
    );
  };

@inject('$sites')
@observer
class InterfaceInventoryToolResult extends Component {
  state = {
    collection: new InterfaceCollection()
  };

  componentDidMount() {
    const { collection } = this.state;
    const { tool } = this.props;
    const { raw_result = {} } = tool;
    collection.add(raw_result);
    this.hydrateLegacyRows();
  }

  // results saved before device_name/site_id were in the search response; set on the model so client sort sees them
  hydrateLegacyRows() {
    const { $sites } = this.props;
    const { collection } = this.state;
    const legacyRows = collection.unfiltered.filter((model) => !model.has('device_name'));

    if (legacyRows.length === 0) {
      return;
    }

    Promise.all([
      deviceIndex.lookupMany(legacyRows.map((model) => model.get('device_id'))),
      $sites.collection.fetch()
    ]).then(([devices]) => {
      const devicesById = keyBy(devices, 'id');

      legacyRows.forEach((model) => {
        const device = devicesById[model.get('device_id')];
        if (device?.device_status === 'V') {
          model.set({
            device_name: device.device_name,
            site_id: device.site_id,
            site_name: $sites.collection.get(device.site_id)?.get('title')
          });
        }
      });
    });
  }

  get columns() {
    const columns = [
      {
        label: 'Admin Status',
        name: 'adminStatus',
        id: 'adminStatus',
        value: 'adminStatus',
        computed: true,
        width: 100,
        renderer: ({ value }) => <InterfaceOperAdminStatusTag value={value} />,
        title: ({ value }) => METRICS_STATUS_LABEL_MAP[value]
      },
      {
        label: 'Oper Status',
        name: 'operStatus',
        id: 'operStatus',
        value: 'operStatus',
        computed: true,
        width: 100,
        renderer: ({ value }) => <InterfaceOperAdminStatusTag value={value} />,
        title: ({ value }) => METRICS_STATUS_LABEL_MAP[value]
      },
      {
        label: 'Interface',
        selectorLabel: 'Interface (combined)',
        id: 'interface',
        value: 'interface',
        sortField: 'interface_description',
        flexBasis: 200,
        minWidth: 100,
        renderer: ({ model }) => {
          const hasOverridden = model.overriddenFields.some((field) =>
            ['snmp_id', 'interface_description', 'snmp_alias'].includes(field)
          );

          if (model.deviceName) {
            return (
              <Box overflow="hidden">
                <InterfaceNameSnmpIdAndDescription
                  interface_description={model.get('interface_description')}
                  snmp_alias={model.get('snmp_alias')}
                  snmp_id={model.get('snmp_id')}
                  device_name={model.deviceName}
                />
                <Overridden isOverridden={hasOverridden} />
              </Box>
            );
          }
          return `Interface (${model.get('snmp_id')}) on archived device (${model.get('device_id')})`;
        }
      },
      {
        label: 'SNMP ID',
        id: 'snmp_id',
        value: 'snmp_id',
        name: 'snmp_id',
        minWidth: 88,
        flexBasis: 50,
        renderer: ColumnOverrideRendererHOC('snmp_id'),
        ellipsis: false
      },
      {
        label: 'Name',
        selectorLabel: 'Interface Name',
        id: 'interface_description',
        value: 'interface_description',
        name: 'interface_description',
        flexBasis: 150,
        renderer: ColumnOverrideRendererHOC('interface_description')
      },
      {
        label: 'Description',
        selectorLabel: 'Interface Description',
        id: 'snmp_alias',
        value: 'snmp_alias',
        name: 'snmp_alias',
        flexBasis: 200,
        renderer: ColumnOverrideRendererHOC('snmp_alias')
      },
      {
        label: 'Device',
        name: 'deviceName',
        id: 'device',
        value: 'device',
        computed: true,
        flexBasis: 115,
        renderer: ({ value }) => (value ? <DeviceLink name={value} /> : <Text muted>None</Text>)
      },
      {
        label: 'Site',
        name: 'siteName',
        id: 'site',
        value: 'site',
        computed: true,
        renderer: ({ value, model }) =>
          value ? <SiteLink siteId={model.siteId}>{value}</SiteLink> : <Text muted>None</Text>
      },
      {
        label: 'Classification',
        name: 'connectivity_type',
        id: 'classification',
        value: 'classification',
        minWidth: 130,
        ellipsis: false,
        renderer: ({ value, model }) => {
          const provider = model.get('provider');
          const networkBoundary = model.get('network_boundary');

          return (
            <InterfaceClassificationKv
              networkBoundary={networkBoundary}
              connectivity={value}
              provider={provider}
              overriddenFields={model.overriddenFields}
              showLinks
            />
          );
        }
      },
      {
        label: 'Capacity',
        name: 'snmp_speed',
        id: 'capacity',
        value: 'capacity',
        flexBasis: 80,
        minWidth: 90,
        renderer: ({ value, model }) => {
          const hasOverridden = model.overriddenFields.includes('snmp_speed');
          const formattedValue = value !== '0' ? formatBytesGreek(value * 1000000, '', 0.9991) : '';
          return (
            <Box>
              <Text as="div">{formattedValue}</Text>
              <Overridden isOverridden={hasOverridden} />
            </Box>
          );
        }
      },
      {
        label: 'IP Address',
        name: 'interface_ip',
        id: 'ip',
        value: 'ip',
        flexBasis: 90,
        minWidth: 100,
        renderer: ColumnOverrideRendererHOC('interface_ip')
      }
    ];

    return columns;
  }

  render() {
    const { collection } = this.state;
    const { tool, onCloseToolResult } = this.props;

    return (
      <ToolResultCard
        tool={tool}
        onCloseToolResult={onCloseToolResult}
        className="tool-result-interface-inventory"
        title={tool.title || 'Interface Inventory Result'}
      >
        <Flex flexDirection="column" px={1} pb={1} flex={1}>
          <AdminTable
            collection={collection}
            useDiscreteFilters={false}
            rowHeight={55}
            columns={this.columns}
            tabPrefId="interfaces"
          />
        </Flex>
      </ToolResultCard>
    );
  }
}

export default InterfaceInventoryToolResult;
